/**
 * wizard - multi-step forms, entry point.
 *
 * Usage:
 *   <form validate wizard former former-endpoint="/x">      // auto-init
 *     <div wizard-step step-title="Account" step-icon="ri-user-line"> ... </div>
 *     <div wizard-step step-title="Review"> ... </div>
 *   </form>
 *   g.wizard('#signup', { vertical: true, onStep(i){...}, beforeNext(i){...} });
 *   g.wizard.get('#signup').back();   // instance API: next is a submit
 *
 * Composes with validator (per-step field rules) and former (the final submit
 * pipeline). Options resolution (later wins): init options < `wizard-*` element
 * attributes. Per-step config: `step-title`, `step-icon` on each `[wizard-step]`.
 * Full reference: docs/libs/wizard.md.
 */
import { Wizard } from './wizard.js';

// Bump on every meaningful change to the library.
const VERSION = '0.1.0';

const camel = (s) => s.replace(/-([a-z])/g, (m, c) => c.toUpperCase());

const coerce = (v) => {
	if (v === '') return true;
	if (v === 'true') return true;
	if (v === 'false') return false;
	if (v !== '' && !isNaN(v) && String(Number(v)) === v) return Number(v);
	return v;
};

// Form-level `wizard-*` attributes -> options (the bare `wizard` marker and the
// per-step `step-*` attributes are read elsewhere, so they are ignored here).
function attrOptions(form) {
	const opts = {};
	for (const attr of form.attributes) {
		let name = null;
		if (attr.name.startsWith('data-wizard-')) name = attr.name.slice(12);
		else if (attr.name.startsWith('wizard-')) name = attr.name.slice(7);
		if (!name) continue;
		if (name === 'back' || name === 'next' || name === 'finish') continue; // authored-button hooks, not options
		opts[camel(name)] = coerce(attr.value);
	}
	return opts;
}

function wizard(target, options) {
	let els;
	if (typeof target === 'string') els = Array.from(document.querySelectorAll(target));
	else if (target instanceof Element) els = [target];
	else els = Array.from(target || []);
	const instances = els.map((form) => form.wizard || new Wizard(form, { ...options, ...attrOptions(form) }));
	return instances.length === 1 ? instances[0] : instances;
}

wizard.version = () => VERSION;
wizard.get = (target) => {
	const el = typeof target === 'string' ? document.querySelector(target) : target;
	return (el && el.wizard) || null;
};

function autoInit() {
	wizard('form[wizard]');
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoInit);
else autoInit();

if (window.g) {
	window.g.wizard = wizard;
	window.g.registerLib('wizard');
} else {
	window.wizard = wizard;
}
